import { ref, readonly, type Ref } from "vue";
import type { AdminProfile } from "@/api/model";
import * as defaultApi from "../api";

interface UseUserDetailReturn {
  readonly user: Ref<AdminProfile | null>;
  readonly isLoading: Ref<boolean>;
  readonly fetchError: Ref<boolean>;
  readonly notFoundError: Ref<boolean>;
  fetchUser: (userId: number) => Promise<void>;
}

export interface UserApi {
  getUser: typeof defaultApi.getUser;
}

/**
 * Composable for fetching and managing user detail data
 */
export function useUserDetail(
  userApi: UserApi | undefined = defaultApi
): UseUserDetailReturn {
  const user = ref<AdminProfile | null>(null);
  const isLoading = ref(false);
  const fetchError = ref(false);
  const notFoundError = ref(false);

  const _handleError = (result: any): void => {
    fetchError.value = true;
    const status = result?.status;

    if (status === 404) {
      notFoundError.value = true;
    }

    if (status === 401 || status === 403) {
      // Handle auth errors if needed
      console.error("Authentication error:", status);
    }

    console.error("API Error fetching user:", result);
  };

  async function fetchUser(userId: number): Promise<void> {
    if (!userId) {
      fetchError.value = true;
      return;
    }

    isLoading.value = true;
    fetchError.value = false;
    notFoundError.value = false;
    user.value = null;

    try {
      const result = await userApi.getUser(userId);

      // Check if the API returned an error
      if ("error" in result) {
        _handleError(result);
      } else {
        // Success - result is the user data directly
        user.value = result as AdminProfile;
      }
    } catch (err) {
      fetchError.value = true;
      console.error("Fetch exception:", err);
    } finally {
      isLoading.value = false;
    }
  }

  return {
    user: readonly(user),
    isLoading: readonly(isLoading),
    fetchError: readonly(fetchError),
    notFoundError: readonly(notFoundError),
    fetchUser,
  };
}
